"use client";

import React, { useState } from "react";
import { Maximize2, X } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger, DialogClose, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const galleryItems = [{
  id: 1,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1557866/img/sabor_ceo_candid_fashion.png",
  alt: "CEOs in multicultural fashion engaging in conversation",
  category: "The People",
  height: "h-[400px]"
}, {
  id: 2,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1557866/img/sabor_twilight_garden_scene.png",
  alt: "Twilight view of the Denver Botanic Gardens with ambient lighting",
  category: "The Atmosphere",
  height: "h-[250px]"
}, {
  id: 3,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1557866/img/sabor_live_latin_musician.png",
  alt: "Live Latin musician performing with passion",
  category: "The Art",
  height: "h-[500px]"
}, {
  id: 4,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1557866/img/sabor_culinary_art_plate.png",
  alt: "Exquisite culinary creation plated like art",
  category: "The Art",
  height: "h-[300px]"
}, {
  id: 5,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1557866/img/sabor_networking_crowd_night.png",
  alt: "High-energy networking crowd at the Ultra Lounge",
  category: "The People",
  height: "h-[350px]"
}, {
  id: 6,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1557866/img/sabor_elegant_toast_champagne.png",
  alt: "Guests sharing an elegant champagne toast",
  category: "The People",
  height: "h-[450px]"
}, {
  id: 7,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1557866/img/sabor_art_installation_detail.png",
  alt: "Detailed view of a vibrant art installation",
  category: "The Art",
  height: "h-[300px]"
}, {
  id: 8,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1557866/img/sabor_vip_lounge_atmosphere.png",
  alt: "Sophisticated atmosphere of the VIP lounge",
  category: "The Atmosphere",
  height: "h-[280px]"
}, {
  id: 9,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1557866/img/sabor_chef_plating_detail.png",
  alt: "Chef meticulously plating a signature dish",
  category: "The Art",
  height: "h-[380px]"
}];
export default function VisualNarrative() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="visual-narrative" className="dark bg-background text-foreground py-24 lg:py-32 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--color-primary)_0%,_transparent_15%)] opacity-10 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {}
        <div className="mb-20 text-center max-w-3xl mx-auto">
          <h2 className="text-primary font-serif text-lg tracking-widest uppercase mb-4">
            The Visual Narrative
          </h2>
          <h3 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-foreground mb-6 leading-tight">
            Where Culture Meets <span className="italic text-primary">Cinema</span>
          </h3>
          <p className="text-muted-foreground text-lg font-sans font-light leading-relaxed">
            A curated glimpse into the high-energy elegance of SABOR DENVER.
            From the candid moments of connection to the artistry of the
            cuisine, witness the story unfold.
          </p>
        </div>

        {}
        <div ref={ref} className={`columns-1 md:columns-2 lg:columns-3 gap-8 space-y-8 transition-opacity duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {galleryItems.map((item, i) => <div key={item.id} data-index={i} className="break-inside-avoid group relative">
              <Dialog>
                <DialogTrigger asChild>
                  <div className="cursor-zoom-in relative overflow-hidden bg-muted/10">
                    {}
                    <div className="absolute inset-0 border border-primary/30 z-20 pointer-events-none transition-colors duration-500 group-hover:border-primary/80" />
                    
                    {}
                    <img src={item.src} alt={item.alt} className="w-full h-auto object-cover transition-transform duration-700 ease-out group-hover:scale-105 filter grayscale-[20%] group-hover:grayscale-0" loading="lazy" />

                    {}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center z-10">
                      <div className="text-center transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                        <span className="text-primary font-serif italic text-xl block mb-2">
                          {item.category}
                        </span>
                        <Maximize2 className="w-6 h-6 text-white mx-auto opacity-80" />
                      </div>
                    </div>
                  </div>
                </DialogTrigger>

                {}
                <DialogContent className="max-w-[95vw] max-h-[95vh] w-auto h-auto p-0 border-none bg-transparent shadow-none sm:max-w-none">
                  <div className="relative w-full h-full flex items-center justify-center">
                    <DialogTitle className="sr-only">{item.alt}</DialogTitle>
                    <DialogDescription className="sr-only">
                      Full size view of {item.alt}
                    </DialogDescription>
                    
                    <img src={item.src} alt={item.alt} className="max-w-full max-h-[90vh] object-contain shadow-2xl border border-primary/20" />
                    
                    {}
                    <DialogClose className="absolute -top-12 right-0 md:-right-12 md:top-0 p-2 text-white/70 hover:text-primary transition-colors bg-black/20 hover:bg-black/40 rounded-full backdrop-blur-sm">
                      <X className="w-8 h-8" />
                      <span className="sr-only">Close</span>
                    </DialogClose>
                  </div>
                </DialogContent>
              </Dialog>
              
              {}
              <div className="mt-3 flex justify-between items-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">
                <span className="text-xs text-muted-foreground uppercase tracking-widest font-sans">
                  {item.category}
                </span>
                <span className="text-xs text-primary/60 font-serif italic">
                  Sabor Denver
                </span>
              </div>
            </div>)}
        </div>

        {}
        <div className="mt-24 text-center">
          <Button variant="outline" className="border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground px-8 py-6 text-lg font-serif tracking-wide uppercase transition-all duration-300">
            View Full Gallery
          </Button>
        </div>
      </div>
    </section>;
}